import {Injectable} from '@angular/core';
import {Action, Store} from '@ngrx/store';
import {Actions, Effect, ofType} from '@ngrx/effects';

import {forkJoin, Observable, of} from 'rxjs';


import {AirComponentType} from '../../../shared';
import {ITriggerBody, ITriggerStore, NotificationSetting, TriggerOperator} from '../../../dashboard/shared';
import {TriggerService} from '../../../dashboard/event/shared';
import {NotificationService} from './../../../dashboard/shared/services/notification.service';

import * as trigger from '../actions/trigger.action';
import * as fromAfbApi from '../../../afb-api/reducers';
import {Device} from '../../../afb-api/models/device';
import {catchError, map, switchMap, tap, withLatestFrom} from 'rxjs/internal/operators';

@Injectable()
export class TriggerEffects {

  @Effect()
  retrieveTrigger$: Observable<Action> = this.actions$.pipe(
    ofType(trigger.RETRIEVE_TRIGGER),
    tap(() => this.afbApistate$.dispatch(new trigger.StartRetrievingTriggerAction())),
    map((action: trigger.RetrieveTriggerAction) => action.payload),
    switchMap((payload: { orgToken: string, deviceList: Device[] }) => {
      const deviceUuidList: string[] = payload.deviceList.map(
        (selectedDevice: Device) => `${selectedDevice.deviceType}_${selectedDevice.deviceId}`
      );

      const obs = [];
      payload.deviceList.map((device: Device) => {
        obs.push(this.notificationService.getNotificationSettings(payload.orgToken, device.deviceType, device.deviceId))
      });
      return forkJoin(obs).pipe(
        map((deviceNotificationSettingList: Array<NotificationSetting[]>) => {

          const newEntities = deviceNotificationSettingList.reduce(
            (entities: { [deviceUuid: string]: ITriggerStore }, notificationSettingList: NotificationSetting[], deviceIndex: number) => {
              return Object.assign(entities, {
                [deviceUuidList[deviceIndex]]: this.notificationSettingListToStore(notificationSettingList)
              });
            }, {});
          return new trigger.RetrieveSuccessAction({entities: newEntities});
        })
      );
    }),
    catchError(() => of(new trigger.RetrieveFailAction()))
  );

  @Effect()
  setTrigger$: Observable<Action> = this.actions$.pipe(
    ofType(trigger.SET_TRIGGER),
    tap(() => this.afbApistate$.dispatch(new trigger.StartSettingTriggerAction())),
    map((action: trigger.SetTriggerAction) => action.payload),
    switchMap((payload: {
      orgToken: string,
      selectedDeviceXTriggerList: Array<{ device: Device, trigger: ITriggerStore }>,
      newTriggerBody: ITriggerBody
    }) => {
      const putObs = [];
      payload.selectedDeviceXTriggerList.forEach((selectedDeviceXTrigger: { device: Device, trigger: ITriggerStore }) => {
        putObs.push(this.notificationService.putNotificationSettings(payload.orgToken,
          selectedDeviceXTrigger.device.deviceType,
          selectedDeviceXTrigger.device.deviceId,
          this.triggerStoreToNotificationSetting(payload.newTriggerBody)));
      });

      return forkJoin(putObs).pipe(
        map((res: any) => new trigger.SetTriggerSuccessAction(payload.orgToken))
      );
    }),
    catchError(() => of(new trigger.SetTriggerFailAction()))
  );

  // Reload trigger settings after new trigger setting is applied
  @Effect()
  setTriggerSuccess$: Observable<Action> = this.actions$.pipe(
    ofType(trigger.SET_TRIGGER_SUCCESS),
    withLatestFrom(this.afbApistate$),
    map(([action, state]: [trigger.SetTriggerSuccessAction, fromAfbApi.State]) => {
      const payload = <string>action.payload;
      const deviceList: Device[] = Object.keys(state.afbApi.device.entities).map((deviceUuid: string) =>
        state.afbApi.device.entities[deviceUuid]);

      return new trigger.RetrieveTriggerAction({
        deviceList,
        orgToken: payload
      });
    })
  );

  // This is to merge multiple trigger objects to single store object

  constructor(private actions$: Actions,
              private afbApistate$: Store<fromAfbApi.State>,
              private triggerService: TriggerService,
              private notificationService: NotificationService) {
  }

  // This makes it easier to map triggers given specific device
  private notificationSettingListToStore(notificationSettingList: NotificationSetting[]): ITriggerStore {

    const triggerBody: {} = notificationSettingList
    // Trigger object has only one element in meta array, and has operator only on temp and humid
      .map((notificationSetting: NotificationSetting) => {
        return {
          airComponentType: <AirComponentType>notificationSetting.comp,
          enabled: notificationSetting.enabled,
          triggerValue: notificationSetting.threshold, // eachTrigger.meta[0].air[Object.keys(eachTrigger.meta[0].air)[0]],
          // Set Operator if it exists
          ...notificationSetting.isRising != null && {
            operator: notificationSetting.isRising ? TriggerOperator.Greater : TriggerOperator.Less
          }
        };
      })
      .reduce((accumulator: ITriggerStore,
               eachTrigger: { airComponentType: AirComponentType, enabled: boolean, triggerValue: number, operator?: TriggerOperator }) => {
        return Object.assign({}, accumulator, {
          [eachTrigger.airComponentType]: {
            value: eachTrigger.triggerValue,
            enabled: eachTrigger.enabled,
            ...eachTrigger.operator != null && {operator: eachTrigger.operator}
          }
        });
      }, {});
    const isTriggerEnabled: boolean = Object.keys(triggerBody)
      .filter((key: string) => triggerBody[key].enabled === true).length > 0;

    // Add hashCode to triggerBody and return
    return {
      ...triggerBody,
      // If triggerBody itself is empty or triggers inside are all disabled, save null to hashCode
      hashCode: JSON.stringify(triggerBody) === '{}' || !isTriggerEnabled ?
        null : this.createHash(JSON.stringify(this.sortEnabledTriggerBody(triggerBody)))
    };
  }

  private createHash(str: string): number {
    let hash = 0, i, chr;

    if (str.length === 0) {
      return hash;
    }
    for (i = 0; i < str.length; i++) {
      chr = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  }

  private triggerStoreToNotificationSetting(newTriggerBody: ITriggerBody): NotificationSetting[] {
    const notificationSettingList: NotificationSetting[] = [];
    Object.keys(AirComponentType).map((key: string) => {
      const airComponent = AirComponentType[key];
      const airComponentTriggerBody = newTriggerBody[airComponent];

      if (airComponent !== AirComponentType.Score) {
        notificationSettingList.push({
          comp: airComponent,
          enabled: airComponentTriggerBody != null ? airComponentTriggerBody.enabled : false,
          // Set threshold to -1 if notification is being disabled
          threshold: (airComponentTriggerBody != null && airComponentTriggerBody.enabled) ? airComponentTriggerBody.value : -1,
          isRising: (airComponentTriggerBody != null && airComponentTriggerBody.operator != null) ?
            airComponentTriggerBody.operator === TriggerOperator.Greater : true
        });
      }
    });

    return notificationSettingList;
  }

  private sortEnabledTriggerBody(triggerBody: ITriggerBody) {
    // Deep copy since it has object inside an object
    const copyTriggerBody = JSON.parse(JSON.stringify(triggerBody));
    // Should convert it to array since stringifying an object can destroy the order of the properties
    const result = Object.keys(copyTriggerBody)
    // Only filter out enabled triggers
      .filter((key: string) => copyTriggerBody[key].enabled === true)
      // Need to sort first to show rule list in proper order
      .sort((a, b) => {
        const mapValue = {temp: 0, humid: 1, co2: 2, voc: 3, dust: 4, pm25: 5, pm10: 6};
        return mapValue[a] - mapValue[b];
      }).map((key: string) => {
        return [key, copyTriggerBody[key]];
      });

    return result;
  }
}
